import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { twMerge } from 'tailwind-merge';
import * as Yup from 'yup';
import {
  type ClientRole,
  createRolePermission,
  getAllPermission,
  getRolePermission,
  getRoles,
  type PatchRolePermissionPayload,
  patchRolePermission,
} from '@/api/roles-and-permission';
import { HasPermission } from '@/components/Permission/HasPermission';
import AccentTitle from '@/components/UI/AccentTitle';
import Button from '@/components/UI/Button';
import { HookForm } from '@/components/UI/HookForm/Form';
import { HookInput } from '@/components/UI/HookForm/Form/Input';
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
import Loading from '@/components/UI/loading';
import Modal from '@/components/UI/Modal';
import { ReactQueryKey } from '@/constants/ReactQueryKeys';
import { authSelector } from '@/store';
import { notifyUser } from '@/store/Toast';
import type { GroupedPermission, PermissionGroup } from '@/types/Permission';
import { canManageLotteryInRolesPage } from '@/utils/lotteryAccess';
import { sanitizeResponseError } from '@/utils';
import { PermissionGrid } from './PermissionGrid';
import {
  convertPermissionsToFormState,
  createPatchPayload,
  getPermissionGroupsForTab,
  type PrevilegesForm,
  stripRestrictedPrivilegesFromPayloads,
  type TProductGroupType,
} from './utils';

type InputObject = Record<string, Record<string, Record<string, boolean>>>;

const validationSchema = Yup.object({
  name: Yup.string()
    .required('Role Name is required')
    .min(2, 'Role must be greater than 2 characters')
    .max(25, 'Role must be less than 25 characters'),
});

type AddRoleType = {
  name: string;
};

const RolesAndPermissionForm = ({
  clientRoles,
  allChecked,
  allUnchecked,
}: {
  clientRoles: ClientRole[];
  allChecked: PrevilegesForm;
  allUnchecked: PrevilegesForm;
}) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { data: currentUser } = useSelector(authSelector);
  const canManageLotteryPrivileges = canManageLotteryInRolesPage(currentUser.isSuper);
  const [currentNav, setCurrentNav] = useState<TProductGroupType>('TERMINAL');
  const {
    handleSubmit,
    getValues,
    formState: { isDirty, dirtyFields },
    reset,
  } = useFormContext<PrevilegesForm>();
  const [show, setShow] = useState<boolean>(false);

  /**
   * !! dirty fields weren't updating as expected, probably because of nested fields
   * !! this function uses dirtyFields and get the latest value using getValues
   * !! need to fix the dirtyFields issue, this is not a scalable solution
   */
  function transformObject(inputObj: PrevilegesForm) {
    // The
    const transformedObj: PrevilegesForm = {};

    // Iterate through each UUID key in the input object
    for (const uuid in inputObj) {
      if (Object.hasOwn(inputObj, uuid)) {
        transformedObj[uuid] = {};

        // Iterate through each key (e.g., "User", "Roles and Privileges") for the current UUID
        for (const key in inputObj[uuid]) {
          if (Object.hasOwn(inputObj[uuid], key)) {
            transformedObj[uuid][key] = {};

            // Iterate through each privilege in the current key
            for (const privilege in inputObj[uuid][key]) {
              if (Object.hasOwn(inputObj[uuid][key], privilege)) {
                // Construct the path using the UUID, key, and privilege
                transformedObj[uuid][key][privilege] = getValues(`${uuid}.${key}.${privilege}`);
              }
            }
          }
        }
      }
    }
    return transformedObj;
  }

  //Tanstack: version 5 query implementation
  const allPermissionQuery = useQuery({ queryKey: ['all-permissions'], queryFn: () => getAllPermission() });

  const permissions = allPermissionQuery.data;

  const permissionKeys = permissions ? (getPermissionGroupsForTab(currentNav, { canManageLotteryPrivileges }) as PermissionGroup[]) : [];

  const newRoleMutation = useMutation(
    (data: { name: string }) =>
      createRolePermission({
        name: data.name,
        privileges: [],
      }),
    {
      onSuccess: () => {
        //Tanstack: version 5 query implementation
        queryClient.invalidateQueries({ queryKey: ['assigned-privileges'] });
        queryClient.invalidateQueries({ queryKey: [ReactQueryKey.GET_ROLES] });
        setShow((prev) => !prev);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'Successfully created role.',
            toastType: 'success',
          })
        );
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );
  const roleMutation = useMutation(
    (payloads: PatchRolePermissionPayload[]) => Promise.all(payloads.map((payload) => patchRolePermission(payload))),
    {
      onSuccess: () => {
        //Tanstack: version 5 query implementation
        queryClient.invalidateQueries({ queryKey: ['assigned-privileges'] });
        queryClient.invalidateQueries({ queryKey: [ReactQueryKey.GET_ROLES] });
        reset(getValues());
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'Permissions updated successfully',
            toastType: 'success',
          })
        );
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const onSaveChanges = () => {
    if (!dirtyFields) return;
    const typedDirtyFields = dirtyFields as PrevilegesForm;

    const fixedDirtyFields = transformObject(typedDirtyFields);
    const payloads = stripRestrictedPrivilegesFromPayloads(createPatchPayload(fixedDirtyFields), {
      canManageLotteryPrivileges,
    });

    roleMutation.mutate(payloads);
  };

  const onCancel = () => {
    reset();
  };

  return (
    <div>
      <div className='flex justify-between w-full items-center border-b pb-5'>
        <div>
          <AccentTitle title='Roles and Permission' />
          <p className='px-5 -mt-5 text-neutralColor-normal'>Choose the role to assign permissions to overall system.</p>
        </div>
        <div className='flex items-center gap-6'>
          {(isDirty || roleMutation.isLoading) && (
            <div className='flex items-center gap-2'>
              <Button label='Cancel' onClick={onCancel} btnStyle='btn-neutral' />
              <Button
                label={roleMutation.isLoading ? 'Please wait...' : 'Save Changes'}
                disabled={roleMutation.isLoading}
                onClick={handleSubmit(onSaveChanges)}
                btnStyle='btn-primary'
              />
            </div>
          )}
          <HasPermission of='CREATE_ROLE'>
            <Button label='Add New Role' btnStyle='btn-primary' customClassName='mr-5' onClick={() => setShow((prev) => !prev)} />
          </HasPermission>
        </div>
      </div>
      <HookForm className='p-5' onSubmit={onSaveChanges}>
        <div className='flex gap-10 font-semibold mb-2 border-b-2 border-neutralColor-lighter'>
          <TabItem value='TERMINAL' isActive={currentNav === 'TERMINAL'} onChange={setCurrentNav} />
          <TabItem value='SHERPA' isActive={currentNav === 'SHERPA'} onChange={setCurrentNav} />
          <TabItem value='ROVER' isActive={currentNav === 'ROVER'} onChange={setCurrentNav} />
        </div>

        <PermissionGrid
          key={currentNav}
          currentNav={currentNav}
          clientRoles={clientRoles}
          permissionKeys={permissionKeys}
          permissions={permissions}
          allChecked={allChecked}
          allUnchecked={allUnchecked}
        />
      </HookForm>
      {show && (
        <Modal>
          <HookFormProvider<AddRoleType> resolver={yupResolver(validationSchema)} mode='all'>
            <HookForm<AddRoleType>
              onSubmit={(values) => {
                newRoleMutation.mutate(values);
              }}
            >
              <h2 className='m-0'>New Role</h2>
              <section className='space-y-5 my-5'>
                <HookInput name='name' type='text' label='Role' placeholder='Enter New Role' />
              </section>
              <div className='w-full flex justify-end gap-4'>
                <Button label='Cancel' btnStyle='btn-neutral' type='button' onClick={() => setShow((prev) => !prev)} />
                <Button
                  label={`${newRoleMutation.isLoading ? 'Adding...' : 'Add'}`}
                  btnStyle='btn-primary'
                  type='submit'
                  disabled={newRoleMutation.isLoading}
                />
              </div>
            </HookForm>
          </HookFormProvider>
        </Modal>
      )}
    </div>
  );
};

export const RolesAndPermission = () => {
  //Tanstack: version 5 query implementation
  const fetchRoles = useQuery({ queryKey: [ReactQueryKey.GET_ROLES], queryFn: () => getRoles() });

  const clientRoles = fetchRoles?.data?.data?.filter((role) => role.name !== 'Super Admin');

  //Tanstack: version 5 query implementation
  const { data, isLoading } = useQuery({
    queryKey: ['assigned-privileges'],
    queryFn: () => {
      return Promise.all((clientRoles ?? []).map((role) => getRolePermission(role.slug)));
    },
    enabled: !!clientRoles?.length,
  });

  if (isLoading) return <Loading loadingText='Loading' />;

  if (data && clientRoles) {
    const { initialState, allChecked, allUnchecked } = convertPermissionsToFormState(data as GroupedPermission[], clientRoles);

    return (
      <HookFormProvider mode='all' defaultValues={initialState}>
        <RolesAndPermissionForm clientRoles={clientRoles} allChecked={allChecked} allUnchecked={allUnchecked} />
      </HookFormProvider>
    );
  }
  return null;
};

function TabItem({
  isActive,
  value,
  onChange,
}: {
  value: TProductGroupType;
  isActive: boolean;
  onChange: (value: TProductGroupType) => void;
}) {
  return (
    <span
      className={twMerge([
        'cursor-pointer p-2 capitalize',
        isActive && 'cursor-pointer border-b-2 border-primaryColor-normal p-2 -mb-0.5 text-primaryColor-normal',
      ])}
      onClick={() => onChange(value)}
    >
      {value.toLowerCase()}
    </span>
  );
}
